import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Box,
    Typography,
    Paper,
    Divider,
} from '@mui/material';
import config from './config.js';

function Profile() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [team, setTeam] = useState('');
    const [roleName, setRoleName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate(); // Initialize navigate
    const token = localStorage.getItem('token'); // Get the token

useEffect(() => {
    if (!token) {
        setError("No token found. Please log in.");
        navigate('/login'); // Redirect to login if no token
        return;
    }

    const fetchProfile = async () => {
        try {
            console.log('Token on useEffect:', token);
            const headers = new Headers();
            headers.append('Authorization', `Bearer ${token}`);
            const response = await fetch(`${config.backendUrl}/profile`, {
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new TypeError("Expected JSON response but got " + contentType);
            }

            const data = await response.json();
            console.log('Profile Data:', data);

            if (response.ok) {
                // Check if the response has the required fields
                if (data && data.name && data.email) {
                    setName(data.name || '');
                    setEmail(data.email || '');
                    setTeam(data.team || 'No team assigned yet.');
                    setRoleName(data.role_name || '');
                } else {
                    setError("Invalid response data format.");
                }
            } else {
                setError(data.message || 'Error fetching profile');
            }
        } catch (error) {
            setError('Network error fetching profile: ' + error.message);
        }
    };

    fetchProfile();
}, [isEditing, token, navigate]); // Added token and navigate to dependency array

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (password && password !== confirmPassword) {
            setError("Passwords don't match");
            return;
        }

        try {
            const response = await fetch(`${config.backendUrl}/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name,
                    email,
                    password,
                }),
            });

            const data = await response.json();
            if (response.ok) {
                setSuccess(data.message || 'Profile updated successfully');
                setIsEditing(false);
                if (password) {
                    setPassword('');
                    setConfirmPassword('');
                }
            } else {
                setError(data.message || 'Error updating profile');
            }
        } catch (error) {
            setError('Network error updating profile');
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    return (
        <Paper
            elevation={3}
            sx={{ padding: 4, maxWidth: 500, margin: '20px auto' }}
        >
            <Typography variant="h5" component="h2" align="center" gutterBottom>
                Your Profile
            </Typography>
            {error && (
                <Typography color="error" align="center">
                    {error}
                </Typography>
            )}
            {success && (
                <Typography color="success" align="center">
                    {success}
                </Typography>
            )}
            <Typography variant="h6" align="left">
                Name: {name}
            </Typography>
            <Typography variant="h6" align="left">
                Email: {email}
            </Typography>
            <Typography variant="h6" align="left">
                Team: {team}
            </Typography>
            <Typography variant="h6" align="left">
                Role: {roleName}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {!isEditing ? (
                <Box textAlign="center">
                    <Button
                        variant="contained"
                        onClick={handleEditClick}
                        borderradius="15px"
                    >
                        Edit Profile
                    </Button>
                </Box>
            ) : (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ mt: 1 }}
                >
                    <TextField
                        margin="normal"
                        fullWidth
                        id="name"
                        label="Name"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        type="email"
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label="New Password (optional)"
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        label="Confirm New Password"
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <Button
                        variant="outlined" // Use outlined style
                        fullWidth
                        sx={{ mt: 3, mb: 1, mr: 2 }} // Add some spacing
                        onClick={() => {
                        setIsEditing(false);
                        setError('');
                        setSuccess('');
                    }}
                >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 1, mb: 2 }}
                    >
                        Update Profile
                    </Button>
                </Box>
            )}
        </Paper>
    );
}

export default Profile;